import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '../SearchBar/SearchBar';
import { useSearchPosts } from '../../hooks/posts/useSearchPosts';
import LoadingEqualizer from 'components/LoadingEqualizer/LoadingEqualizer';
import { getCategoryConfig } from 'utils/categories';
import { useCourseId } from 'hooks/course/useCourseId';

const PostSearch: React.FC = () => {
  const courseId = useCourseId();
  const { posts, loading, error, searchPosts } = useSearchPosts(courseId);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Abre dropdown ao buscar — memoizado
  const handleSearch = useCallback(
    (query: string) => {
      searchPosts(query);
      setIsOpen(!!query);
    },
    [searchPosts]
  );

  return (
    <div className="md:relative md:w-full md:max-w-md" ref={dropdownRef}>
      <SearchBar onSearch={handleSearch} />

      {isOpen && (
        <div className="absolute mt-1 left-0 w-full bg-white border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto">
          {loading && <LoadingEqualizer/>}
          {!loading && !error && posts.length === 0 && (
            <p className="text-gray-500 text-sm p-3">Nenhum post encontrado</p>
          )}
          {posts.length > 0 && (
            <ul className="divide-y">
              {posts.map((post) => {
                const categoryConfig = getCategoryConfig(post.category);

                return (
                  <li key={post.id}>
                    <Link
                      to={`/aluna/posts/${post.id}`}
                      onClick={() => setIsOpen(false)}
                      className="block p-3 hover:bg-gray-100 transition"
                    >
                      <h3 className="font-semibold text-sm">{post.title}</h3>
                      <div
                        className="text-gray-600 text-xs line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        por {post.submittedBy.name}
                        {categoryConfig && (
                          <span
                            className="ml-1 px-1 py-1 rounded-full text-[10px] font-medium"
                            style={{
                              backgroundColor: `${categoryConfig.color}20`,
                              color: categoryConfig.color,
                            }}
                          >
                            {categoryConfig.icon} {categoryConfig.label}
                          </span>
                        )}
                      </p>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PostSearch;
